import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut } from "../../../services/api";
import { safeGetInventory, safeGetSuppliers, safeGetSupplierAgeing } from "../../../services/safeApi";
import { emitFinanceEvent } from "../finance/financeDispatcher";

import SupplierLedger from "./SupplierLedger";
import AddBillModal from "./AddBillModal";
import RecordPaymentModal from "./RecordPaymentModal";
import LowStockDashboard from "./LowStockDashboard";
import SkeletonLoader from "../../common/SkeletonLoader";

import "./InventoryManager.css";

interface InventoryItem {
  id: number;
  name: string;
  category?: string;
  is_low_stock?: boolean;
}

interface Supplier {
  id: number;
  name: string;
  mobile?: string;
  balance_due?: number;
  supplier_type?: string;
}

export default function InventoryManager() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [ageing, setAgeing] = useState<unknown[]>([]);

  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  const [showAddBillModal, setShowAddBillModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemModalData, setItemModalData] = useState<InventoryItem | null>(null);
  const [itemForm, setItemForm] = useState({ name: "", category: "General" });

  const [supplierModal, setSupplierModal] = useState<{ open: boolean; data: Supplier | null }>({ open: false, data: null });
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    mobile: ""
  });

  useEffect(() => {
    refreshData();
  }, [reload]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const [inv, sup, age] = await Promise.all([
        apiGet("/api/manager/clinic-inventory"), // Changed from safeGetInventory which fetched drugs
        safeGetSuppliers(),
        safeGetSupplierAgeing(),
      ]);

      setItems((inv ?? []) as InventoryItem[]);
      setSuppliers((sup ?? []) as Supplier[]);
      setAgeing(age ?? []);

      if (!selectedSupplier && (sup ?? []).length > 0) {
        setSelectedSupplier((sup as Supplier[])[0]);
      }
    } catch (e) {
      console.error("Inventory load failed", e);
      setItems([]);
      setSuppliers([]);
      setAgeing([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAlert = async (id: number) => {
    try {
      await apiPost("/api/manager/inventory/toggle-alert", { id });
      setReload(p => !p);
    } catch (e) {
      console.error("Inventory sync failed", e);
      alert("Inventory sync failed. Backend may not be ready.");
    }
  };

  const openSupplierModal = (supplier: Supplier | null = null) => {
    setSupplierForm({
      name: supplier?.name ?? "",
      mobile: supplier?.mobile ?? ""
    });
    setSupplierModal({ open: true, data: supplier });
  };

  const saveItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!itemForm.name.trim()) return alert("Item Name is required");

    try {
      if (itemModalData?.id) {
        await apiPut(`/api/manager/inventory/${itemModalData.id}`, itemForm);
      } else {
        await apiPost("/api/manager/inventory", itemForm);
      }
      setShowItemModal(false);
      setItemModalData(null);
      setItemForm({ name: "", category: "General" });
      setReload(p => !p);
      alert(itemModalData?.id ? "✅ Item Updated" : "✅ Item Added");
    } catch (e) {
      alert("Failed to add item");
    }
  };

  const openItemModal = (item: InventoryItem | null = null) => {
    setItemModalData(item);
    setItemForm({
      name: item?.name ?? "",
      category: item?.category ?? "General",
    });
    setShowItemModal(true);
  };

  const deleteItem = async (id: number, name: string) => {
    if (!window.confirm(`Delete item "${name}"?`)) return;
    try {
      await apiPost("/api/manager/inventory/delete", { id });
      setReload(p => !p);
    } catch (e) {
      alert("Failed to delete item");
    }
  };

  const saveSupplier = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!supplierForm.name.trim()) return alert("Name is required");

    try {
      await apiPost("/api/manager/suppliers", {
        id: supplierModal.data?.id,
        name: supplierForm.name,
        mobile: supplierForm.mobile
      });
      setSupplierModal({ open: false, data: null });
      setReload(p => !p);
    } catch (e) {
      alert("Failed to save supplier");
    }
  };

  const deleteSupplier = async (id: number, name: string) => {
    if (!window.confirm(`Delete supplier "${name}"?`)) return;
    try {
      await apiPost("/api/manager/suppliers/delete", { supplier_id: id });
      setReload(p => !p);
      if (selectedSupplier?.id === id) setSelectedSupplier(null);
    } catch (e) {
      alert("Cannot delete: Supplier has outstanding balance.");
    }
  };


  const totalDue = suppliers.reduce((sum, s) => sum + Number(s.balance_due || 0), 0);

  return (
    <div className="inventory-container">

      <div className="inv-header-row">
        <div className="inv-title-group">
          <h2>📦 Clinical Inventory & Clinic Suppliers</h2>
          <span className="due-badge">Outstanding: ₹{totalDue.toLocaleString()}</span>
        </div>

        <div className="inv-actions">
          <button className="btn-secondary" type="button" onClick={() => openItemModal(null)}>
            🧪 New Material
          </button>

          <button className="btn-secondary" type="button" onClick={() => openSupplierModal(null)}>
            🏭 New Supplier
          </button>
          <button className="btn-primary" type="button" onClick={() => setShowAddBillModal(true)}>
            🧾 Add Bill
          </button>
          <button className="btn-primary" type="button" onClick={() => setShowPaymentModal(true)}>
            💸 Record Payment
          </button>
        </div>
      </div>

      <div className="inv-dashboard-grid">

        <div className="panel supplier-panel">
          <div className="panel-header"><h4>🏭 Clinic Suppliers</h4></div>
          <div className="supplier-list-container">
            {loading ? (
              <SkeletonLoader variant="list" rows={5} />
            ) : suppliers.map(s => (
              <div
                key={s.id}
                className={`apexo-folder-card ${selectedSupplier?.id === s.id ? "active" : ""}`}
                style={{ marginBottom: '12px' }}
                onClick={() => setSelectedSupplier(s)}
              >
                <div className="si-info">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong>{s.name}</strong>
                    <span style={{
                      fontSize: "0.7rem",
                      background: "#f3f4f6",
                      color: "#4b5563",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontWeight: "600"
                    }}>
                      Clinical
                    </span>
                  </div>
                  <span>{s.mobile || "—"}</span>
                </div>
                <div className="si-meta">
                  <span className={`balance-tag ${(s.balance_due ?? 0) > 0 ? "due" : "paid"}`}>
                    {(s.balance_due ?? 0) > 0 ? `Due: ₹${s.balance_due}` : "Settled"}
                  </span>
                  <div className="si-actions">
                    <button className="btn-icon edit" type="button" onClick={(e) => { e.stopPropagation(); openSupplierModal(s); }}>✏️</button>
                    <button className="btn-icon delete" type="button" onClick={(e) => { e.stopPropagation(); deleteSupplier(s.id, s.name); }}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel ledger-panel">
          {selectedSupplier ? (
            <SupplierLedger supplier={selectedSupplier} />
          ) : (
            <div className="empty-state-large">👈 Select a supplier</div>
          )}
        </div>

        <div className="panel right-panel">

          <div className="widget-box">
            <LowStockDashboard items={items} onResolve={toggleAlert} />
            {items.filter(i => i.is_low_stock).length === 0 && (
              <div className="good-stock-msg">✅ All items in stock</div>
            )}
          </div>

          <div className="widget-box flex-grow">
            <h4>📋 Master Material List</h4>
            <div className="mini-stock-list">
              {loading ? (
                <SkeletonLoader variant="list" rows={4} />
              ) : items.length === 0 ? (
                <div className="muted">No items yet. Click "+ New Material" to add.</div>
              ) : (
                items.map(i => (
                  <div key={i.id} className="mini-stock-row">
                    <span>{i.name}</span>
                    <div className="mini-stock-actions">
                      <button
                        className={i.is_low_stock ? "btn-restock" : "btn-low"}
                        type="button"
                        onClick={() => toggleAlert(i.id)}
                      >
                        {i.is_low_stock ? "Restocked" : "Low"}
                      </button>
                      <button className="btn-icon edit" type="button" onClick={() => openItemModal(i)}>✏️</button>
                      <button className="btn-icon delete" type="button" onClick={() => deleteItem(i.id, i.name)}>🗑️</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>

      {showItemModal && (
        <div className="modal-overlay">
          <form className="modal-card" onSubmit={saveItem}>
            <h3>{itemModalData ? "✏️ Edit Material" : "📦 Add New Material"}</h3>
            <div className="form-group">
              <label>Item Name</label>
              <input
                value={itemForm.name}
                onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g. Latex Gloves, Bonding Agent"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={itemForm.category}
                onChange={e => setItemForm({ ...itemForm, category: e.target.value })}
              >
                <option value="General">General</option>
                <option value="Consumable">Consumable</option>
                <option value="Instrument">Instrument</option>
                <option value="Chemical">Chemical</option>
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-text" onClick={() => setShowItemModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary">Add Item</button>
            </div>
          </form>
        </div>
      )}

      {showAddBillModal && (
        <AddBillModal
          suppliers={suppliers}
          onClose={() => setShowAddBillModal(false)}
          onSaved={() => {
            setShowAddBillModal(false);
            setReload(p => !p);
          }}
        />
      )}

      {showPaymentModal && (
        <RecordPaymentModal
          suppliers={suppliers}
          onClose={() => setShowPaymentModal(false)}
          onPaid={() => {
            setShowPaymentModal(false);
            setReload(p => !p);
          }}
        />
      )}

      {supplierModal.open && (
        <div className="modal-overlay">
          <form className="modal-card" onSubmit={saveSupplier}>
            <h3>{supplierModal.data ? "Edit Clinic Supplier" : "Add Clinic Supplier"}</h3>
            <div className="form-group">
              <label>Name</label>
              <input
                value={supplierForm.name}
                onChange={e => setSupplierForm({ ...supplierForm, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input
                value={supplierForm.mobile}
                onChange={e => setSupplierForm({ ...supplierForm, mobile: e.target.value })}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-text" onClick={() => setSupplierModal({ open: false, data: null })}>Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}
